import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import { doc, getDoc, setDoc } from "firebase/firestore";
import countryData from "../data/country.json";
import countryCodeData from "../data/countryCode.json";

const AddCountryModal = ({ onAddSuccess }) => {
  const [selectedCountryName, setSelectedCountryName] = useState("");
  const [mappedCode, setMappedCode] = useState("");
  const [mappedDialCode, setMappedDialCode] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [loading, setLoading] = useState(false);

  // Auto-map code and dialCode when selectedCountryName changes
  useEffect(() => {
    if (!selectedCountryName) {
      setMappedCode("");
      setMappedDialCode("");
      return;
    }

    const countryInfo = countryData.find(
      (c) => c.name.toLowerCase() === selectedCountryName.toLowerCase()
    );

    if (countryInfo) {
      // name = country.json.name
      // code = country.json.isoCode (uppercase)
      const code = (countryInfo.isoCode || "").toUpperCase();
      setMappedCode(code);

      // dialCode = match from countryCode.json where country matches
      const codeInfo = countryCodeData.find(
        (cc) => cc.country.toLowerCase() === countryInfo.name.toLowerCase()
      );
      setMappedDialCode(codeInfo ? codeInfo.code : "");
    } else {
      setMappedCode("");
      setMappedDialCode("");
    }
  }, [selectedCountryName]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedCountryName || !mappedCode) {
      setErrorMsg("Please select a valid country.");
      return;
    }

    setErrorMsg("");
    setSuccessMsg("");
    setLoading(true);

    try {
      // 1. Prevent duplicate: check if country already exists in Firestore using code as doc ID
      const countryDocRef = doc(db, "countries", mappedCode);
      const docSnap = await getDoc(countryDocRef);

      if (docSnap.exists()) {
        setErrorMsg(`❌ ${selectedCountryName} (${mappedCode}) is already added.`);
        setLoading(false);
        return;
      }

      // 2. Save logic: Save to Firestore using document ID = country code
      await setDoc(countryDocRef, {
        code: mappedCode,
        name: selectedCountryName,
        dialCode: mappedDialCode,
        enabled: true, // enabled = true (default)
      });

      setSuccessMsg("✅ Country added successfully!");
      setSelectedCountryName("");
      setMappedCode("");
      setMappedDialCode("");

      // Trigger list refresh
      if (onAddSuccess) {
        onAddSuccess();
      }

      // Automatically close modal after a short delay
      setTimeout(() => {
        setSuccessMsg("");
        const closeBtn = document.querySelector("#addCountryModal .btn-close");
        if (closeBtn) {
          closeBtn.click();
        }
      }, 1500);

    } catch (error) {
      console.error("Error adding country:", error);
      setErrorMsg("❌ Failed to add country. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Sort countryData alphabetically by name
  const sortedCountryData = [...countryData].sort((a, b) =>
    a.name.localeCompare(b.name)
  );

  return (
    <div
      className="modal fade"
      id="addCountryModal"
      tabIndex="-1"
      aria-labelledby="addCountryModalLabel"
      aria-hidden="true"
    >
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content rounded-4 shadow border-0 theme-modal-content">
          <div className="modal-header border-0 pb-0">
            <h5 className="text-h1 fw-bold w-100 text-center mt-3" id="addCountryModalLabel" style={{ color: 'var(--accent-lime)' }}>
              Add New Country
            </h5>
            <button
              type="button"
              className="btn-close position-absolute end-0 top-0 m-3"
              data-bs-dismiss="modal"
              aria-label="Close"
            ></button>
          </div>

          <div className="modal-body px-4 pb-4">
            {errorMsg && (
              <div className="alert alert-danger py-2 rounded-3 small" role="alert">
                {errorMsg}
              </div>
            )}
            {successMsg && (
              <div className="alert alert-success py-2 rounded-3 small" role="alert">
                {successMsg}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              {/* Country Dropdown */}
              <div className="mb-3">
                <label htmlFor="countrySelect" className="form-label fw-semibold theme-form-label">
                  Select Country
                </label>
                <select
                  id="countrySelect"
                  className="form-select border-0 shadow-sm py-2"
                  style={{ backgroundColor: "#FFFFFF", borderRadius: "8px" }}
                  value={selectedCountryName}
                  onChange={(e) => setSelectedCountryName(e.target.value)}
                  required
                  disabled={loading}
                >
                  <option value="">Choose a country...</option>
                  {sortedCountryData.map((country, idx) => (
                    <option key={idx} value={country.name}>
                      {country.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Mapped Country Code */}
              <div className="mb-3">
                <label className="form-label fw-semibold theme-form-label">Country Code</label>
                <input
                  type="text"
                  className="form-control border-0 shadow-sm py-2 bg-white text-muted"
                  style={{ borderRadius: "8px" }}
                  value={mappedCode}
                  placeholder="Auto-mapped from country data"
                  readOnly
                />
              </div>

              {/* Mapped Dial Code */}
              <div className="mb-4">
                <label className="form-label fw-semibold theme-form-label">Dial Code</label>
                <input
                  type="text"
                  className="form-control border-0 shadow-sm py-2 bg-white text-muted"
                  style={{ borderRadius: "8px" }}
                  value={mappedDialCode}
                  placeholder="Auto-mapped from country codes"
                  readOnly
                />
              </div>

              {/* Action Buttons */}
              <div className="d-grid gap-2">
                <button
                  type="submit"
                  className="btn btn-primary py-2 rounded-3 fw-semibold shadow-sm"
                  disabled={loading || !selectedCountryName}
                >
                  {loading ? (
                    <>
                      <span
                        className="admin-loader-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Saving...
                    </>
                  ) : (
                    "Save Country"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddCountryModal;
